import { Link } from "react-router-dom";
import {
  ArrowRight,
  Code,
  Brain,
  Palette,
  Zap,
  CheckCircle,
  Database,
  FileText,
  Scan,
  UserCheck,
  Globe,
} from "lucide-react";
import { PageHero } from "../components/PageHero";

export const Services = () => {
  const services = [
    {
      id: "software-development",
      title: "Software Development & AI",
      description:
        "Transform your digital presence with full-stack development and AI solutions. Our experienced engineering teams deliver scalable web applications, mobile apps, and intelligent automation systems that drive real business growth.",
      icon: Code,
      gradient: "from-green-500 to-green-600",
      bgPattern: "bg-gradient-to-br from-green-50 to-green-100",
      image: "/images/Web Development.jpg",
      features: [
        "Full-stack Web Development (React, Node.js, Python, Java)",
        "AI/ML Engineering & Model Development",
        "Blockchain Development & Smart Contracts",
        "API Integration & Microservices Architecture",
        "Cloud Infrastructure & DevOps",
        "Mobile App Development (iOS/Android)",
      ],
      technologies: [
        "React",
        "Node.js",
        "Python",
        "TensorFlow",
        "AWS",
        "Blockchain",
      ],
      caseStudies: [
        {
          title: "E-commerce Platform Rebuild",
          client: "US Fashion Retailer",
          result:
            "40% increase in conversions with optimized user experience. Delivered 60% faster than local alternatives.",
        },
        {
          title: "Real-time Analytics Dashboard",
          client: "FinTech Startup",
          result:
            "Reduced data processing time by 95% with AI-powered automation. Now handles 10x transaction volume.",
        },
      ],
    },
    {
      id: "ai-data-services",
      title: "AI Data Services & Annotation",
      description:
        "Power your machine learning models with precise data annotation and curation services. Our specialized teams handle computer vision, NLP datasets, and audio transcription with industry-leading accuracy for healthcare, automotive, and technology companies.",
      icon: Brain,
      gradient: "from-green-500 to-green-600",
      bgPattern: "bg-gradient-to-br from-green-50 to-green-100",
      image: "/images/data-processing.jpeg",
      features: [
        "Computer Vision Data Labeling & Annotation",
        "Natural Language Processing Dataset Creation",
        "Audio Transcription & Speech Recognition Training",
        "Quality Assurance & Data Validation",
        "Custom Dataset Creation & Curation",
        "ML Model Training & Optimization",
      ],
      technologies: [
        "CVAT",
        "Labelbox",
        "Python",
        "OpenCV",
        "spaCy",
        "Hugging Face",
      ],
      caseStudies: [
        {
          title: "Medical Imaging AI Training",
          client: "German Healthcare Tech",
          result:
            "Achieved 95.2% diagnostic accuracy through precise annotation of 50K medical images. Delivered in 3 weeks vs 8-week timeline.",
        },
        {
          title: "Autonomous Vehicle Dataset",
          client: "Silicon Valley Automotive",
          result:
            "Labeled 1.2M street scenes with 99.8% accuracy, improving pedestrian detection by 40% across lighting conditions.",
        },
      ],
    },
    {
      id: "digital-media",
      title: "Digital Media & Marketing",
      description:
        "Elevate your brand with compelling visual content and strategic digital marketing. Our creative teams produce engaging videos, design stunning visuals, and execute performance-driven campaigns that convert audiences into customers.",
      icon: Palette,
      gradient: "from-green-500 to-green-600",
      bgPattern: "bg-gradient-to-br from-green-50 to-green-100",
      image: "/images/Graphics Design.jpg",
      features: [
        "Video Production & Motion Graphics",
        "Social Media Content Creation & Management",
        "Brand Identity & UI/UX Design",
        "Digital Marketing Campaign Strategy",
        "Content Writing & Copywriting",
        "SEO & Performance Marketing",
      ],
      technologies: [
        "After Effects",
        "Premiere Pro",
        "Figma",
        "Photoshop",
        "Google Ads",
        "Analytics",
      ],
      caseStudies: [
        {
          title: "Product Launch Campaign",
          client: "Canadian EdTech Startup",
          result:
            "300% brand awareness increase through strategic video content. Generated 2M+ views and 15K qualified signups.",
        },
        {
          title: "B2B Content Strategy",
          client: "UK SaaS Platform",
          result:
            "150% increase in qualified leads through strategic storytelling that transforms technical features into compelling narratives.",
        },
      ],
    },
    {
      id: "data-collection-entry",
      title: "Data Collection & Entry",
      description:
        "Transform your raw data into actionable insights with our comprehensive data collection and entry services. Our skilled teams handle everything from survey data collection to form processing with 99.9% accuracy rates.",
      icon: Database,
      gradient: "from-green-500 to-green-600",
      bgPattern: "bg-gradient-to-br from-green-50 to-green-100",
      image: "/images/bpo/data-collection.jpeg",
      features: [
        "Survey Data Collection & Processing",
        "Form Data Entry & Validation",
        "Database Management & Updates",
        "Data Quality Assurance & Verification",
        "Custom Data Collection Forms",
        "Real-time Data Processing & Reporting",
      ],
      technologies: [
        "Excel",
        "Google Forms",
        "Airtable",
        "MySQL",
        "Python",
        "R",
      ],
      caseStudies: [
        {
          title: "Market Research Data Collection",
          client: "US Market Research Firm",
          result:
            "Collected and processed 50K survey responses with 99.8% accuracy. Delivered insights 3x faster than traditional methods.",
        },
        {
          title: "Customer Database Migration",
          client: "European E-commerce Platform",
          result:
            "Migrated 100K customer records with zero data loss. Improved data quality by 40% through validation processes.",
        },
      ],
    },
    {
      id: "data-sorting-processing",
      title: "Data Sorting & Processing",
      description:
        "Organize and process your data with precision and efficiency. Our teams specialize in complex data sorting, categorization, and processing workflows that turn chaotic information into structured, usable datasets.",
      icon: FileText,
      gradient: "from-green-500 to-green-600",
      bgPattern: "bg-gradient-to-br from-green-50 to-green-100",
      image: "/images/bpo/data-processing.jpeg",
      features: [
        "Data Categorization & Classification",
        "Automated Data Sorting Algorithms",
        "Data Cleaning & Deduplication",
        "Custom Processing Workflows",
        "Data Format Conversion & Standardization",
        "Bulk Data Processing & Batch Operations",
      ],
      technologies: [
        "Python",
        "Pandas",
        "SQL",
        "Apache Spark",
        "Excel",
        "Power BI",
      ],
      caseStudies: [
        {
          title: "Financial Transaction Processing",
          client: "Canadian FinTech Company",
          result:
            "Processed 1M+ transactions daily with 99.95% accuracy. Reduced processing time by 70% through automated workflows.",
        },
        {
          title: "Inventory Data Organization",
          client: "UK Retail Chain",
          result:
            "Sorted and categorized 500K product records. Improved inventory accuracy by 60% and reduced stock discrepancies.",
        },
      ],
    },
    {
      id: "data-digitization",
      title: "Data Digitization",
      description:
        "Convert your physical documents and legacy data into digital formats with our advanced digitization services. We handle everything from paper documents to microfilm with OCR technology and manual verification for perfect accuracy.",
      icon: Scan,
      gradient: "from-orange-500 to-red-600",
      bgPattern: "bg-gradient-to-br from-orange-50 to-red-50",
      image: "/images/bpo/data-digitization.jpeg",
      features: [
        "Document Scanning & OCR Processing",
        "Legacy System Data Migration",
        "Microfilm & Microfiche Digitization",
        "Handwritten Document Transcription",
        "Image Enhancement & Quality Control",
        "Digital Archive Creation & Management",
      ],
      technologies: [
        "OCR",
        "Tesseract",
        "Adobe Acrobat",
        "Python",
        "ImageMagick",
        "Cloud Storage",
      ],
      caseStudies: [
        {
          title: "Medical Records Digitization",
          client: "German Healthcare System",
          result:
            "Digitized 200K patient records with 99.9% accuracy. Improved access time from hours to seconds.",
        },
        {
          title: "Legal Document Archive",
          client: "US Law Firm",
          result:
            "Converted 50K legal documents to searchable digital format. Reduced document retrieval time by 90%.",
        },
      ],
    },
    {
      id: "remote-kyc-service",
      title: "Remote KYC Service",
      description:
        "Streamline your customer onboarding with our comprehensive Know Your Customer (KYC) services. Our trained professionals handle identity verification, document validation, and compliance checks with industry-leading accuracy and speed.",
      icon: UserCheck,
      gradient: "from-green-500 to-green-600",
      bgPattern: "bg-gradient-to-br from-green-50 to-green-100",
      image: "/images/bpo/kyc-service.jpeg",
      features: [
        "Identity Document Verification",
        "Address & Contact Validation",
        "Compliance & Risk Assessment",
        "Biometric Data Processing",
        "Sanctions & PEP Screening",
        "Real-time Verification & Reporting",
      ],
      technologies: [
        "ID Verification APIs",
        "Blockchain",
        "Machine Learning",
        "Compliance Tools",
        "CRM Systems",
      ],
      caseStudies: [
        {
          title: "Banking Customer Onboarding",
          client: "African Digital Bank",
          result:
            "Processed 10K KYC applications with 99.7% accuracy. Reduced onboarding time from 5 days to 2 hours.",
        },
        {
          title: "Crypto Exchange Compliance",
          client: "European Crypto Platform",
          result:
            "Verified 25K users across 15 countries. Achieved 100% regulatory compliance with zero false positives.",
        },
      ],
    },
    {
      id: "data-scraping",
      title: "Data Scraping",
      description:
        "Extract valuable data from websites, social media, and online platforms with our ethical and efficient data scraping services. We provide clean, structured data that powers your business intelligence and market research initiatives.",
      icon: Globe,
      gradient: "from-green-500 to-green-600",
      bgPattern: "bg-gradient-to-br from-green-50 to-green-100",
      image: "/images/bpo/data-scraping.jpeg",
      features: [
        "Web Scraping & Data Extraction",
        "Social Media Data Collection",
        "E-commerce Product Information",
        "Real Estate & Property Data",
        "News & Content Aggregation",
        "API Integration & Custom Solutions",
      ],
      technologies: [
        "Python",
        "Scrapy",
        "BeautifulSoup",
        "Selenium",
        "APIs",
        "Data Cleaning Tools",
      ],
      caseStudies: [
        {
          title: "Competitor Price Monitoring",
          client: "US E-commerce Retailer",
          result:
            "Monitored 50K products across 20 competitors daily. Increased profit margins by 15% through dynamic pricing.",
        },
        {
          title: "Market Research Data",
          client: "UK Investment Firm",
          result:
            "Collected 1M+ data points from financial websites. Provided real-time market insights for investment decisions.",
        },
      ],
    },
  ];

  const processSteps = [
    {
      step: "01",
      title: "Discover Your Needs",
      description:
        "We spend time understanding what you actually need, not just what you think you want. Then we match you with people who've solved similar problems before.",
      icon: "🔍",
    },
    {
      step: "02",
      title: "Honor & Match Talent",
      description:
        "Meet your team members. Video calls, portfolio reviews, and trial projects help you see who you're actually working with—not just their resumes.",
      icon: "🤝",
    },
    {
      step: "03",
      title: "Build Relationships",
      description:
        "Start working together in 2-week sprints. Regular check-ins, clear communication, and shared project management tools keep everyone aligned.",
      icon: "🌍",
    },
    {
      step: "04",
      title: "Create Shared Value",
      description:
        "Get your project delivered on time and budget. Plus, you've helped create meaningful employment for professionals building their careers in East Africa.",
      icon: "💎",
    },
  ];

  return (
    <div>
      <PageHero
        title="Expert Services, East African Excellence"
        subtitle="Partner with specialized teams across Uganda, Kenya, and Rwanda to deliver high-quality results at competitive rates. From software development to AI services, we connect you with the right expertise."
      />

      {/* Services Detail Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => (
              <div
                key={service.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? "lg:grid-flow-col-dense" : ""}`}
              >
                <div
                  className={`${index % 2 === 1 ? "lg:col-start-2" : ""} animate-fadeInLeft`}
                >
                  <div className="inline-flex items-center px-4 py-2 rounded-full bg-brand-secondary border border-gray-200 text-brand-grey mb-6">
                    <service.icon className="w-5 h-5 mr-2" />
                    <span className="text-sm font-semibold">
                      Expert Service
                    </span>
                  </div>

                  <h2 className="text-3xl md:text-4xl font-bold text-brand-primary mb-6">
                    {service.title}
                  </h2>

                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-brand-grey mb-4">
                      Core Capabilities
                    </h3>
                    <ul className="space-y-3">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Technologies */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-brand-grey mb-4">
                      Technologies & Tools
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {service.technologies.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="px-3 py-1 rounded-full text-sm font-medium bg-white shadow-sm border border-gray-200 text-gray-600"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  <Link
                    to="/contact"
                    className={`inline-flex items-center px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 bg-gradient-to-r ${service.gradient} text-white shadow-lg hover:shadow-xl`}
                  >
                    Start Your {service.title.split(" ")[0]} Project
                    <ArrowRight className="ml-2" size={16} />
                  </Link>
                </div>

                <div
                  className={`${index % 2 === 1 ? "lg:col-start-1" : ""} animate-fadeInRight`}
                >
                  <div className="relative">
                    <div className="bg-white border border-gray-200 rounded-3xl shadow-2xl p-6 transform rotate-2 hover:rotate-0 transition-all duration-500">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-64 object-cover rounded-2xl mb-6"
                      />

                      {/* Case Studies */}
                      <div className="space-y-4">
                        <h4 className="text-lg font-bold text-brand-grey">
                          Recent Success Stories
                        </h4>
                        {service.caseStudies.map((study, studyIndex) => (
                          <div
                            key={studyIndex}
                            className="border-l-4 border-green-500 pl-4"
                          >
                            <h5 className="font-semibold text-brand-grey">
                              {study.title}
                            </h5>
                            <p className="text-sm text-gray-500">
                              {study.client}
                            </p>
                            <p className="text-sm font-medium text-green-600">
                              {study.result}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Floating Badge */}
                    <div className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center text-white font-bold text-sm shadow-xl transform rotate-12 hover:rotate-0 transition-all duration-500">
                      Expert
                      <br />
                      Level
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fadeInUp">
            <div className="inline-flex items-center px-6 py-3 rounded-full bg-brand-secondary border border-gray-200 text-brand-grey text-sm font-semibold mb-6">
              <Zap className="w-4 h-4 mr-2" />
              Our Proven Process
            </div>
            <h2 className="text-4xl font-bold text-brand-primary mb-6">
              How We Work Together
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From first call to project delivery, here's exactly what happens
              when you work with us.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div
                key={index}
                className={`text-center animate-fadeInUp`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative mb-6">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-xl">
                    {step.step}
                  </div>
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-3xl">
                    {step.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-brand-grey mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="/images/cta-1.jpeg"
            alt="African professionals collaborating"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/90 via-brand-tertiary/85 to-brand-primary/90"></div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full animate-float"></div>
        <div
          className="absolute bottom-10 right-10 w-16 h-16 bg-white/10 rounded-full animate-float"
          style={{ animationDelay: "1s" }}
        ></div>
        <div
          className="absolute top-1/2 left-1/4 w-12 h-12 bg-white/10 rounded-full animate-float"
          style={{ animationDelay: "2s" }}
        ></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="animate-fadeInUp">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Join companies getting projects done faster with our East African
              teams. Let's talk about what you need built.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link
                to="/contact"
                className="bg-white text-green-600 px-8 py-4 rounded-2xl text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
              >
                Start Your Partnership
              </Link>
              <Link
                to="/portfolio"
                className="border-2 border-white text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-white hover:text-green-600 transition-all duration-300 transform hover:-translate-y-1"
              >
                See Impact Stories
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
